import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { isMobileDevice } from '#app/shared/lib/is-mobile-device.js'

const WHATSAPP_PHONE = '34621199059'

function getWhatsAppUrl(message: string, isMobile: boolean) {
  const sanitizedMessage = encodeURIComponent(message.trim())
  if (isMobile) {
    return `https://wa.me/${WHATSAPP_PHONE}?text=${sanitizedMessage}`
  }

  return `https://web.whatsapp.com/send?phone=${WHATSAPP_PHONE}&text=${sanitizedMessage}`
}

function WhatsAppIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={`
        h-7 w-7 text-white

        md:h-9 md:w-9
      `}
    >
      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.07 0C5.51 0 .17 5.34.17 11.9c0 2.1.55 4.15 1.59 5.95L0 24l6.34-1.67a11.88 11.88 0 0 0 5.73 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.46-8.41ZM12.08 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.76.99 1-3.66-.24-.37a9.88 9.88 0 0 1-1.52-5.26c0-5.47 4.45-9.92 9.92-9.92 2.65 0 5.14 1.03 7.01 2.91a9.86 9.86 0 0 1 2.9 7c0 5.47-4.45 9.92-9.9 9.92Zm5.45-7.43c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15s-.77.97-.94 1.17c-.18.2-.35.22-.65.08-.3-.15-1.26-.46-2.4-1.47a9.05 9.05 0 0 1-1.67-2.06c-.17-.3-.02-.45.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.63-.92-2.23-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.08-.79.38-.27.3-1.04 1.02-1.04 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.23 1.35.2 1.86.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      height="24"
      width="24"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
    >
      <title>wds-ic-send-filled</title>
      <path
        d="M5.4 19.425C5.06667 19.5583 4.75 19.5291 4.45 19.3375C4.15 19.1458 4 18.8666 4 18.5V14L12 12L4 9.99997V5.49997C4 5.1333 4.15 4.85414 4.45 4.66247C4.75 4.4708 5.06667 4.44164 5.4 4.57497L20.8 11.075C21.2167 11.2583 21.425 11.5666 21.425 12C21.425 12.4333 21.2167 12.7416 20.8 12.925L5.4 19.425Z"
        fill="currentColor"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
      <path
        d="M18 6 6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckSVG() {
  return (
    <svg viewBox="0 0 16 15" width="16" height="15">
      <path
        fill="currentColor"
        d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"
      />
    </svg>
  )
}

export default function WhatsAppFloatingButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDelay, setIsDelay] = useState(true)
  const [message, setMessage] = useState('')
  const [hasPlayedIntro, setHasPlayedIntro] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const delayTimeoutRef = useRef<number | null>(null)

  const openWhatsApp = (text: string) => {
    if (typeof window === 'undefined') return
    const url = getWhatsAppUrl(text, isMobileDevice())
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!message.trim()) return
    openWhatsApp(message)
  }

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target || containerRef.current?.contains(target)) return

      setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen])

  const timeNow = useMemo(
    () =>
      new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    [],
  )

  useEffect(() => {
    if (!isOpen) return

    if (hasPlayedIntro) {
      setIsDelay(false)
      return
    }

    setIsDelay(true)
    delayTimeoutRef.current = window.setTimeout(() => {
      setIsDelay(false)
      setHasPlayedIntro(true)
    }, 2000)

    return () => {
      if (delayTimeoutRef.current) {
        window.clearTimeout(delayTimeoutRef.current)
      }
    }
  }, [hasPlayedIntro, isOpen])

  return (
    <div
      ref={containerRef}
      className="pointer-events-none fixed bottom-5 right-5 z-[110] font-sans"
    >
      <div
        className={`
          mb-3 w-[376px] max-w-[calc(100vw-28px)] origin-bottom-right
          overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white
          shadow-[0_7px_28px_rgba(0,0,0,0.25)] transition-all duration-300
          ease-out

          ${
            isOpen
              ? `
                pointer-events-auto max-h-[520px] translate-y-0 scale-100
                opacity-100
              `
              : 'pointer-events-none max-h-0 translate-y-4 scale-95 opacity-0'
          }
        `}
      >
        <header
          className={`
            flex items-center justify-between bg-[#0B7C6F] px-3 py-3 text-white
          `}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div
                className={`
                  flex h-14 w-14 items-center justify-center overflow-hidden
                  rounded-full border border-white/20 bg-white
                `}
              >
                <img
                  src={'/iso-go-light.png'}
                  width="60"
                  height="60"
                  alt="logo gama"
                  className="h-11 w-11 object-contain"
                />
              </div>
              <span
                className={`
                  absolute bottom-[1px] right-[1px] h-[11px] w-[11px]
                  rounded-full border-2 border-[#0B7C6F] bg-[#5AD16A]
                `}
              />
            </div>
            <div className="leading-tight">
              <p className="text-lg font-semibold leading-5">Gama Óptima</p>
              <p className="mt-1 text-xs text-white/90">
                Te responderemos lo antes posible
              </p>
            </div>
          </div>
          <button
            type="button"
            className={`
              rounded-full p-1 text-white/90 transition

              hover:bg-white/10
            `}
            aria-label="Cerrar chat de WhatsApp"
            onClick={() => setIsOpen(false)}
          >
            <CloseIcon />
          </button>
        </header>

        <div
          className="px-5 py-4 pb-14"
          style={{
            backgroundImage: `url(/img/whatsapp-chat-bg.png)`,
            backgroundRepeat: 'repeat',
            backgroundSize: '270px',
            backgroundColor: '#ece5dd',
          }}
        >
          {isDelay ? (
            <div
              className={`
                inline-flex items-center rounded-full bg-white px-4 py-3
                shadow-sm
              `}
            >
              <span
                className={`
                  mr-2 h-3 w-3 animate-bounce rounded-full bg-[#8a8f93]
                `}
              />
              <span
                className={`
                  mr-2 h-3 w-3 animate-bounce rounded-full bg-[#8a8f93]

                  [animation-delay:150ms]
                `}
              />
              <span
                className={`
                  h-3 w-3 animate-bounce rounded-full bg-[#8a8f93]

                  [animation-delay:300ms]
                `}
              />
            </div>
          ) : (
            <div className="max-w-[250px] rounded-lg bg-[#f9f9f9] p-3 shadow-sm">
              <p
                className={`mb-1 text-xs font-semibold leading-5 text-[#a2a2a2]`}
              >
                Gama Óptima
              </p>
              <p className="text-sm leading-[1.45] text-[#303030]">
                ¡Hola! Encantados de saludarte 🤝
                <br />
                ¿En qué podemos ayudarte?
              </p>
              <p
                className={`
                  mt-2 flex items-center justify-end text-[11px] text-[#999]
                `}
              >
                {timeNow}
                <span className="ml-1 text-[#8a8f93]">
                  <CheckSVG />
                </span>
              </p>
            </div>
          )}
        </div>

        <footer className="border-t border-[#e7ecef] bg-[#f0f2f5] px-3 py-3">
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <input
              type="text"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              className={`
                h-[45px] flex-1 rounded-3xl border border-[#e1e5e8] bg-white
                px-4 text-[14px] text-[#111b21] outline-none transition

                focus:border-[#d0d7db]

                placeholder:text-[#8e9aa3]
              `}
              placeholder="Escribe un mensaje..."
            />
            <button
              type="submit"
              disabled={!message.trim()}
              className={`
                flex h-10 w-10 items-center justify-center rounded-full
                bg-[#25D366] text-white transition

                hover:bg-[#1ea952]
              `}
              aria-label="Enviar mensaje por WhatsApp"
            >
              <SendIcon />
            </button>
          </form>
        </footer>
      </div>

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`
          pointer-events-auto relative ml-auto flex h-[52px] w-[52px]
          items-center justify-center rounded-full bg-[#25D366]
          shadow-[0_10px_28px_rgba(37,211,102,0.55)] transition

          hover:scale-[1.03] hover:bg-[#1ea952]

          md:h-[62px] md:w-[62px]
        `}
        aria-label="Abrir chat de WhatsApp"
      >
        <span
          className={`absolute inset-[-10px] -z-10 rounded-full bg-[#25D366]/18`}
        />
        <WhatsAppIcon />
        {!isOpen && (
          <span
            className={`
              absolute right-0 top-0 flex h-4 min-w-4 items-center
              justify-center rounded-full bg-[#ff3b30] px-1 text-[10px]
              font-bold text-white

              md:-right-1 md:-top-1 md:h-5 md:min-w-5 md:text-[11px]
            `}
          >
            1
          </span>
        )}
      </button>
    </div>
  )
}
